/* eslint-disable no-unused-vars */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import useTime from './useTime';
import { FEATURE } from '../utils/feature';

const hasFeature = FEATURE();

const LAST_EXECUTION_TIME_VARIABLE = 'reload-visits-executed-at';

const MockFn = () => {};

const CacheExpiryDuration = 10; // in seconds.

/**
 * @param {{
*    tasksInfo: {
*      data: {
*        Id: string,
*      }[],
*      called: boolean,
*      loading: boolean,
*      lastSyncTime: number;
*    };
* }} props 
* @returns {void}
*/
export const useVisits = ({
  scheduledVisits = {},
  asyncOrders = {},
  headerInfoAPI = {},
  tasksInfo = {},

  getTasks = async () => {},
  getPrescriber = async () => {},
  getScheduledVisits = async () => {},
  getPendingVisits = async () => {},
}) => {
  const userId = useMemo(() => localStorage.getItem('userId'), []);
  /**
   * @description can cause performance issue. temporary disabled feature
   */
  /*
  const currentTime = useTime({ interval: 30 });
  const intervalCounter = useRef(-1);
  */

  const isCacheExpired = useCallback((props) => {
    /**
     * @type {{
     *  lastSyncTime: number;
     * }}
     */
    const { lastSyncTime } = props;
    const time = new Date().getTime();
    const timeDifference = time - lastSyncTime;
    const differenceInSeconds = parseInt(timeDifference / 1000);
    return differenceInSeconds > CacheExpiryDuration;
  }, []);

  useEffect(() => {
    if (tasksInfo.loading) {
      return MockFn;
    }

    if (!tasksInfo.called) {
      getTasks();
      return MockFn;
    }

    if (tasksInfo?.lastSyncTime === 0) {
      getTasks();
      return MockFn;
    }

    if (isCacheExpired(tasksInfo)) {
      getTasks();
      return MockFn;
    }

    return MockFn;
  }, [tasksInfo, isCacheExpired]);

  useEffect(() => { 
    if (!userId) {
      return false;
    }

    if (headerInfoAPI.loading === false && headerInfoAPI.called === false) {
      getPrescriber();
    }
  }, [headerInfoAPI, userId]);

  const syncVisits = useCallback((props) => {
    /**
     * @type {{
     *  modality: {
     *    visits: {
     *      loading: boolean;
     *      called: boolean;
     *    };
     *    lastSyncTime: number;
     *  };
     *  userId: number;
     *  fetchVisits: () => void;
     * }}
     */
    const { modality, userId, fetchVisits } = props;

    if (!userId) {
      return MockFn;
    }

    if (modality?.visits?.loading) {
      return MockFn;
    }

    if (!modality?.visits?.called) {
      fetchVisits();
      return MockFn;
    }

    if (modality?.lastSyncTime === 0) {
      fetchVisits();
      return MockFn;
    }

    if (isCacheExpired(modality)) {
      fetchVisits();
      return MockFn;
    }

    return MockFn;
  }, [isCacheExpired]);

  useEffect(() => {
    syncVisits({
      modality: scheduledVisits,
      userId,
      fetchVisits: getScheduledVisits,
    });
  }, [scheduledVisits?.visits, userId, syncVisits]);

  useEffect(() => {
    syncVisits({
      modality: asyncOrders,
      userId,
      fetchVisits: getPendingVisits,
    }); 
  }, [asyncOrders?.visits, userId, syncVisits]);

  /**
   * @description can cause performance issue. temporary disabled feature
   */
  /*
  useEffect(() => {
    if (!hasFeature.AUTO_RELOAD_VISITS) {
      return false;
    }

    if (!localStorage.getItem(LAST_EXECUTION_TIME_VARIABLE)) {
      localStorage.setItem(LAST_EXECUTION_TIME_VARIABLE, currentTime.epochTime);
    }

    intervalCounter.current += 1;

    if (intervalCounter.current === 0) {
      return false;
    }

    const lastExecutionTime = Number(localStorage.getItem(LAST_EXECUTION_TIME_VARIABLE));
    const timeDifference = (currentTime.epochTime - lastExecutionTime) / 1000;

    // when time difference is more than 3 minutes
    if (timeDifference >= 60 * 3) {
      localStorage.setItem(LAST_EXECUTION_TIME_VARIABLE, currentTime.epochTime);
      getScheduledVisits();
      getPendingVisits();
    }
  }, [currentTime]);
  */
};

export default useVisits;
